import React from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'

interface AboutData {
  storyText: string
  title: string
  description: string
  location?: string
  locationLabel?: string
  hours?: Array<{ days: string; time: string }>
  backgroundImage: string
}

interface AboutProps {
  data: AboutData
}

const About: React.FC<AboutProps> = ({ data }) => {
  const navigate = useNavigate()

  return (
    <section className="w-full pt-4 pb-8 sm:py-12 lg:py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="relative grid grid-cols-1 lg:grid-cols-2 items-center gap-10">

          {/* Image */}
          <div
            className="h-[260px] sm:h-[360px] lg:h-[520px] rounded-xl bg-cover bg-center"
            style={{ backgroundImage: `url(${data.backgroundImage})` }}
            aria-hidden
          />

          {/* Content */}
          <div className="relative lg:-ml-24">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              viewport={{ once: true }}
              className="bg-[#252527] rounded-xl shadow-2xl p-6 sm:p-8"
            >

              {/* Title */}
              <h2
                className="
                  font-display font-bold text-white
                  text-[clamp(1.6rem,4.5vw,3rem)]
                  leading-[1.15]
                  break-words
                  max-w-xl
                  mb-4
                "
              >
                {data.title}
              </h2>

              {/* Divider */}
              <div className="mb-4 h-[2px] w-16 bg-gray-600" />

              {/* Description - Show preview only */}
              <div className="relative max-w-prose">
                <p
                  className="
                    text-gray-100
                    text-[clamp(0.9rem,1.3vw,1.05rem)]
                    leading-[1.6]
                    break-words
                    line-clamp-2
                  "
                >
                  {data.description}
                </p>
              </div>

              {/* Read More Button - Navigate to About Page */}
              <button
                onClick={() => navigate('/about')}
                className="
                  mt-6 inline-flex items-center
                  text-primary font-medium
                  text-[clamp(0.75rem,1.1vw,0.9rem)]
                  leading-[1.4]
                  transition-colors
                  hover:text-primary/80
                  focus:outline-none
                  focus-visible:ring-2 focus-visible:ring-primary/40
                  rounded
                "
              >
                Read More →
              </button>

            </motion.div>
          </div>

        </div>
      </div>
    </section>
  )
}

export default About
